import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CONSTANTS } from '../../Constants/Constants';

export type DateFormat = 'date' | 'time' | 'dateTime' | 'dateTimeWithSeconds';

@Pipe({
  name: 'multilingualDate',
  pure: true
})
export class MultilingualDatePipe implements PipeTransform {
  constructor(private readonly translateService: TranslateService) {}

  /**
   * Transforms a date value into a locale-formatted string.
   * @param value - Date, string, or number to format
   * @param format - 'date' | 'time' | 'dateTime' | 'dateTimeWithSeconds'
   */
  transform(value: Date | string | number | null | undefined, format: DateFormat = 'date', assumeUtc: boolean = true): string {
    if(value === null || value === undefined || value === CONSTANTS.EMPTY_STRING) {
      return CONSTANTS.EMPTY_STRING;
    }
    const date = this.parseDate(value, assumeUtc);
    if (!date || Number.isNaN(date.getTime())) {
      return value.toString();
    }
    const currentLang = this.translateService.currentLang || this.translateService.defaultLang || CONSTANTS.LANGUAGE_EN_US;
    try {
      if (currentLang === CONSTANTS.LANGUAGE_FR_CA) {
        return this.formatFrenchCanadian(date, format);
      } else if (currentLang === CONSTANTS.LANGUAGE_EN_CA) {
        return this.formatEnglishCanadian(date, format);
      } else {
        return this.formatEnglish(date, format);
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return date.toLocaleDateString();
    }
  }
  private parseDate(value: Date | string | number, assumeUtc: boolean = true): Date |null {
    if(value instanceof Date) {
      return value;
    }
    if(typeof value === 'number') {
      return new Date(value);
    }
    if(typeof value === 'string') {
      const localeResult = this.parseFallbackDate(value , assumeUtc);
      if (localeResult) {
        return localeResult;
      }
      const parseValue = (assumeUtc && this.isIsoWithoutTimezone(value)) ? value + 'Z' : value;
      const timestamp = Date.parse(parseValue);
      if (!Number.isNaN(timestamp)) {
        return new Date(timestamp);
      }
      return null;
    }
    return null;
  }

  private isIsoWithoutTimezone(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(value);
}

  private parseFallbackDate(value: string, assumeUtc: boolean = true): Date | null {
    const dateParts = this.parseLocaleDate(value);
    if (!dateParts) {
      return null;
    }
    const { first, second, year } = dateParts;
    const { hours, minutes, seconds } = this.parseTimePart(value);
    const hasTimePart = value.indexOf(' ') !== -1 && value.substring(value.indexOf(' ') + 1).trim().length > 0;

    let result: Date;
    if (first > 12) {
      result = (hasTimePart && assumeUtc) ? new Date(Date.UTC(year, second - 1, first, hours, minutes, seconds)) : new Date(year, second - 1, first, hours, minutes, seconds);
    } else if (second > 12) {
      result = (hasTimePart && assumeUtc) ? new Date(Date.UTC(year, first - 1, second, hours, minutes, seconds)) : new Date(year, first - 1, second, hours, minutes, seconds);
    } else {
      result = (hasTimePart && assumeUtc) ? new Date(Date.UTC(year, second - 1, first, hours, minutes, seconds)) : new Date(year, second - 1, first, hours, minutes, seconds);
    }
    return Number.isNaN(result.getTime()) ? null : result;
  }

  private parseLocaleDate(value: string): { first: number, second: number, year: number } | null {
    const datePart = value.split(' ')[0];
    const separator = this.getDateSeparator(datePart);
    if (!separator) {
      return null;
    }
    const parts = datePart.split(separator);
    if (parts.length !== 3) {
      return null;
    }
    if (parts[0].length < 1 || parts[0].length > 2
      || parts[1].length < 1 || parts[1].length > 2
      || parts[2].length !== 4) {
      return null;
    }
    const first = Number.parseInt(parts[0], 10);
    const second = Number.parseInt(parts[1], 10);
    const year = Number.parseInt(parts[2], 10);
    if (Number.isNaN(first) || Number.isNaN(second) || Number.isNaN(year)) {
      return null;
    }
    return { first, second, year };
  }

  private parseTimePart(value: string): { hours: number, minutes: number, seconds: number } {
    const defaultTime = { hours: 0, minutes: 0, seconds: 0 };
    const spaceIndex = value.indexOf(' ');
    if (spaceIndex === -1) {
      return defaultTime;
    }
    const timePart = value.substring(spaceIndex + 1).trim();
    if (!timePart) {
      return defaultTime;
    }
    const parts = timePart.split(':');
    if (parts.length < 2) {
      return defaultTime;
    }
    const hours = Number.parseInt(parts[0], 10);
    const minutes = Number.parseInt(parts[1], 10);
    const seconds = parts.length > 2 ? Number.parseInt(parts[2], 10) : 0;
    return {
      hours: Number.isNaN(hours) ? 0 : hours,
      minutes: Number.isNaN(minutes) ? 0 : minutes,
      seconds: Number.isNaN(seconds) ? 0 : seconds
    };
  }

  private getDateSeparator(value: string): string | null {
    if (value.includes('-')) {
      return '-';
    }
    if (value.includes('/')) {
      return '/';
    }
    return null;
  }

  private formatEnglish(date: Date, format: DateFormat): string {
    switch (format) {
      case 'date':
        return this.buildEnglishDate(date);
      case 'time':
        return this.buildEnglishTime(date);
      case 'dateTime':
        return `${this.buildEnglishDate(date)}, ${this.buildEnglishTime(date)}`;
      case 'dateTimeWithSeconds':
        return `${this.buildEnglishDate(date)}, ${this.buildEnglishTimeWithSeconds(date)}`;
      default:
        return this.buildEnglishDate(date);
    }
  }

  private formatEnglishCanadian(date: Date, format: DateFormat): string {
    switch (format) {
      case 'date':
        return this.buildCanadianDate(date);
      case 'time':
        return this.buildEnglishTime(date);
      case 'dateTime':
        return `${this.buildCanadianDate(date)}, ${this.buildEnglishTime(date)}`;
      case 'dateTimeWithSeconds':
        return `${this.buildCanadianDate(date)}, ${this.buildEnglishTimeWithSeconds(date)}`;
      default:
        return this.buildCanadianDate(date);
    }
  }

  private formatFrenchCanadian(date: Date, format: DateFormat): string {
    switch (format) {
      case 'date':
        return this.buildCanadianDate(date);
      case 'time':
        return this.buildFrenchCanadianTime(date);
      case 'dateTime':
        return `${this.buildCanadianDate(date)} ${this.buildFrenchCanadianTime(date)}`;
      case 'dateTimeWithSeconds':
        return `${this.buildCanadianDate(date)} ${this.buildFrenchCanadianTimeWithSeconds(date)}`;
      default:
        return this.buildCanadianDate(date);
    }
  }

  private buildEnglishDate(date: Date): string {
    const parts = new Intl.DateTimeFormat(CONSTANTS.LANGUAGE_EN_US, {
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(date);
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    const year = parts.find(p => p.type === 'year')?.value;
    return `${month}/${day}/${year}`;
  }

  private buildEnglishTime(date: Date): string {
    return new Intl.DateTimeFormat(CONSTANTS.LANGUAGE_EN_US, {
      hour: 'numeric', minute: '2-digit', hour12: true
    }).format(date);
  }

  private buildEnglishTimeWithSeconds(date: Date): string {
    return new Intl.DateTimeFormat(CONSTANTS.LANGUAGE_EN_US, {
      hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: false
    }).format(date);
  }

  private buildCanadianDate(date: Date): string {
    const parts = new Intl.DateTimeFormat(CONSTANTS.LANGUAGE_FR_CA, {
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(date);
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    return `${year}-${month}-${day}`;
  }

  private buildFrenchCanadianTime(date: Date): string {
    const parts = new Intl.DateTimeFormat(CONSTANTS.LANGUAGE_FR_CA, {
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(date);
    const hour = parts.find(p => p.type === 'hour')?.value;
    const minute = parts.find(p => p.type === 'minute')?.value;
    return `${hour}:${minute}`;
  }
  private buildFrenchCanadianTimeWithSeconds(date: Date): string {
    const parts = new Intl.DateTimeFormat(CONSTANTS.LANGUAGE_FR_CA, {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }).formatToParts(date);
    const hour = parts.find(p => p.type === 'hour')?.value;
    const minute = parts.find(p => p.type === 'minute')?.value;
    const second = parts.find(p => p.type === 'second')?.value;
    return `${hour}:${minute}:${second}`;
  }

}
